import React from "react";
import { motion } from "framer-motion";
import { FileDown } from "lucide-react";

interface ExportItem {
  date: string;
  NAUBurn: number;
  NAUYMint: number;
  NAUNMint: number;
}

interface ExportButtonProps {
  data: ExportItem[];
}

const ExportButton: React.FC<ExportButtonProps> = ({ data }) => {
  const hasData = Array.isArray(data) && data.length > 0;

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCSV = () => {
    if (!hasData) return;
    const header = "Date,NAUburn,NAUYmint,NAUNmint";
    const rows = data.map(
      ({ date, NAUBurn, NAUYMint, NAUNMint }) =>
        `${date},${NAUBurn},${NAUYMint},${NAUNMint}`
    );
    const csv = [header, ...rows].join("\n");
    downloadBlob(new Blob([csv], { type: "text/csv" }), "ucwaves_stats.csv");
  };

  const handleJSON = () => {
    if (!hasData) return;
    const json = JSON.stringify(data, null, 2);
    downloadBlob(
      new Blob([json], { type: "application/json" }),
      "ucwaves_stats.json"
    );
  };

  const handleXLSX = async () => {
    if (!hasData) return;
    try {
      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "UCWaves Stats");
      XLSX.writeFile(wb, "ucwaves_stats.xlsx");
    } catch (error) {
      console.error("❌ Failed to export XLSX:", error);
    }
  };

  const baseBtn =
    "flex items-center gap-2 px-5 py-3 font-semibold text-lg rounded-2xl shadow transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2";

  return (
    <motion.div
      role="region"
      aria-labelledby="export-button-title"
      className="mb-10 flex flex-col gap-4 justify-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div>
        {" "}
        <h2
          id="export-button-title"
          className="text-4xl font-bold text-red-800 mb-4 text-cente py-10 text-center"
        >
          Export Data
        </h2>
        {/* Description Card */}
        <div className="flex-1 bg-white rounded-lg shadow p-6 max-w-6xl flex items-center">
          <p className="text-gray-700 text-sm leading-relaxed">
            Download your current filtered dataset to conduct offline analysis
            or integrate into reporting tools. Useful for governance research,
            trend tracking, and building future predictive models based on
            community behavior.
          </p>
        </div>
        <div className="flex flex-wrap gap-4 justify-center md:mt-15">
          {" "}
          <button
            onClick={handleCSV}
            disabled={!hasData}
            title="Export CSV"
            className={`${baseBtn} bg-blue-900 text-white hover:bg-blue-800 focus:ring-blue-700`}
          >
            <FileDown size={18} />
            CSV
          </button>
          <button
            onClick={handleJSON}
            disabled={!hasData}
            title="Export JSON"
            className={`${baseBtn} bg-gray-700 text-white hover:bg-gray-600 focus:ring-gray-500`}
          >
            <FileDown size={18} />
            JSON
          </button>
          <button
            onClick={handleXLSX}
            disabled={!hasData}
            title="Export Excel"
            className={`${baseBtn} bg-green-700 text-white hover:bg-green-600 focus:ring-green-500`}
          >
            <FileDown size={18} />
            Excel
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ExportButton;
